import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { addFamilyRelationship } from "../../store/thunks/clientsThunk";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import { logInfo, logError } from "../../utils/logger";
import QuickAddClientModal from "../../modals/QuickAddClientModal";

export default function AddFamilyMemberModal({
  memberType,
  clientId,
  onSave = () => {},
  onClose,
  onCancel,
  onUpdateClient,
}) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [showQuickAddParentModal, setShowQuickAddParentModal] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const dispatch = useDispatch();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCancel();
        logInfo("AddFamilyMemberModal closed with Escape key", {
          memberType,
          clientId,
        });
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel, memberType, clientId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedClient) {
      setFeedback({
        type: "error",
        message: "Please select a family member.",
      });
      return;
    }

    const relationshipData =
      memberType === "parent"
        ? { parentId: selectedClient._id, childId: clientId }
        : { parentId: clientId, childId: selectedClient._id };

    try {
      const result = await dispatch(
        addFamilyRelationship(relationshipData)
      ).unwrap();

      if (
        result?.message === "Parent-child relationship updated successfully"
      ) {
        setFeedback({
          type: "success",
          message: `Successfully linked ${selectedClient.firstName} ${selectedClient.lastName}`,
        });
        onSave?.({ selectedClient, relation: memberType });
        await onUpdateClient();

        setTimeout(() => {
          onClose();
        }, 1500);

        logInfo("Family relationship added successfully", {
          memberType,
          clientId,
          selectedClientId: selectedClient._id,
          relationshipData,
        });
      } else {
        setFeedback({
          type: "error",
          message: "Failed to link family member. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error updating family relationship:", error);
      setFeedback({
        type: "error",
        message:
          error.message || "Failed to link family member. Please try again.",
      });
      logError("Error updating family relationship", {
        memberType,
        clientId,
        selectedClientId: selectedClient._id,
        relationshipData,
        error: error.message,
      });
    }
  };

  const handleClientSelection = (client) => {
    setSelectedClient(client);
    logInfo("Family member selected in AddFamilyMemberModal", {
      memberType,
      clientId,
      selectedClientId: client._id,
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-sm shadow-lg w-[400px] max-w-[95vw] p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {feedback.message && (
            <div
              className={`p-3 rounded-sm text-sm ${
                feedback.type === "success"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
              {feedback.message}
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Link {memberType === "parent" ? "Parent" : "Child"}
            </h2>
            {/* <p className="text-sm text-gray-500">
              Link the {memberType} to the client.
            </p> */}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Search for a {memberType}
            </label>
            <div>
              <SearchDropdown
                prefix=":client:"
                displayFields={["firstName", "lastName", "email"]}
                onSelection={handleClientSelection}
                placeholder={`Start typing to search`}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex h-8 items-center justify-center rounded-sm border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100">
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex h-8 items-center justify-center rounded-sm bg-indigo-600 px-4 text-sm font-medium text-white transition-colors hover:bg-indigo-500">
              Link {memberType} to client
            </button>
          </div>

          {memberType === "parent" && (
            <button
              type="button"
              onClick={() => setShowQuickAddParentModal(true)}
              className="inline-flex h-8 items-center justify-center w-full rounded-sm border border-indigo-200 bg-white px-4 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-50">
              Can't find the right parent? Add a new one
            </button>
          )}
        </form>

        {showQuickAddParentModal && (
          <QuickAddClientModal
            addParent={true}
            onClose={() => {
              setShowQuickAddParentModal(false);
              onUpdateClient();
            }}
            onSave={(newParent) => {
              setSelectedClient(newParent);
              setShowQuickAddParentModal(false);
              onUpdateClient();
            }}
          />
        )}
      </div>
    </div>
  );
}
